// web/admin/src/features/leaderboards/api.ts
import { apiClient } from "../../lib/apiClient";
import type {
  AuditEntry,
  AuditListFilter,
  Bucket,
  CreateLeaderboardInput,
  CreateRewardInput,
  Cycle,
  Entry,
  Leaderboard,
  ListFilter,
  Reward,
  ScoreAdjustInput,
  UpdateLeaderboardInput,
  UpdateRewardInput,
} from "./types";

function basePath(gameId: string) {
  return `/api/admin/projects/${encodeURIComponent(gameId)}/leaderboards`;
}

function lbPath(gameId: string, lbId: string) {
  return `${basePath(gameId)}/${encodeURIComponent(lbId)}`;
}

function cyclePath(gameId: string, lbId: string, cycleId: string) {
  return `${lbPath(gameId, lbId)}/cycles/${encodeURIComponent(cycleId)}`;
}

function scorePath(gameId: string, lbId: string, cycleId: string, entityId: string) {
  return `${cyclePath(gameId, lbId, cycleId)}/scores/${encodeURIComponent(entityId)}`;
}

function rewardPath(gameId: string, lbId: string, rid: string) {
  return `${lbPath(gameId, lbId)}/rewards/${encodeURIComponent(rid)}`;
}

// ===== Leaderboards =====

export async function listLeaderboards(
  gameId: string,
  filter: ListFilter = {},
): Promise<Leaderboard[]> {
  const qs = new URLSearchParams();
  if (filter.status) qs.set("status", filter.status);
  if (filter.window_kind) qs.set("window_kind", filter.window_kind);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<{ leaderboards: Leaderboard[] }>(
    `${basePath(gameId)}${suffix}`,
  );
  return r.data.leaderboards;
}

export async function getLeaderboard(gameId: string, lbId: string): Promise<Leaderboard> {
  const r = await apiClient.get<Leaderboard>(lbPath(gameId, lbId));
  return r.data;
}

export async function createLeaderboard(
  gameId: string,
  input: CreateLeaderboardInput,
): Promise<Leaderboard> {
  const r = await apiClient.post<Leaderboard>(basePath(gameId), input);
  return r.data;
}

export async function updateLeaderboard(
  gameId: string,
  lbId: string,
  input: UpdateLeaderboardInput,
): Promise<Leaderboard> {
  const r = await apiClient.patch<Leaderboard>(lbPath(gameId, lbId), input);
  return r.data;
}

export async function archiveLeaderboard(gameId: string, lbId: string): Promise<void> {
  await apiClient.delete(lbPath(gameId, lbId));
}

// ===== Cycles =====

export interface ListCyclesPage {
  cycles: Cycle[];
  cursor: string;
}

export async function listCycles(
  gameId: string,
  lbId: string,
  opts: { cursor?: string; limit?: number } = {},
): Promise<ListCyclesPage> {
  const qs = new URLSearchParams();
  if (opts.cursor) qs.set("cursor", opts.cursor);
  if (opts.limit) qs.set("limit", String(opts.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<ListCyclesPage>(
    `${lbPath(gameId, lbId)}/cycles${suffix}`,
  );
  return r.data;
}

export async function getCycle(
  gameId: string,
  lbId: string,
  cycleId: string,
): Promise<Cycle> {
  const r = await apiClient.get<Cycle>(cyclePath(gameId, lbId, cycleId));
  return r.data;
}

export async function listBuckets(
  gameId: string,
  lbId: string,
  cycleId: string,
): Promise<Bucket[]> {
  const r = await apiClient.get<{ buckets: Bucket[] }>(
    `${cyclePath(gameId, lbId, cycleId)}/buckets`,
  );
  return r.data.buckets;
}

export interface TopResult {
  leaderboard: Leaderboard;
  cycle: Cycle;
  entries: Entry[];
  cursor: string;
}

export interface ListTopOpts {
  limit?: number;
  cursor?: string;
  bucket_id?: string;
  tier_key?: string;
  guild_id?: string;
  expand?: string[];
}

export async function listCycleTop(
  gameId: string,
  lbId: string,
  cycleId: string,
  opts: ListTopOpts = {},
): Promise<TopResult> {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set("limit", String(opts.limit));
  if (opts.cursor) qs.set("cursor", opts.cursor);
  if (opts.bucket_id) qs.set("bucket_id", opts.bucket_id);
  if (opts.tier_key) qs.set("tier_key", opts.tier_key);
  if (opts.guild_id) qs.set("guild_id", opts.guild_id);
  if (opts.expand?.length) qs.set("expand", opts.expand.join(","));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const r = await apiClient.get<TopResult>(
    `${cyclePath(gameId, lbId, cycleId)}/top${suffix}`,
  );
  return r.data;
}

// ===== Score moderation =====

export async function deleteScore(
  gameId: string,
  lbId: string,
  cycleId: string,
  entityId: string,
): Promise<void> {
  await apiClient.delete(scorePath(gameId, lbId, cycleId, entityId));
}

export async function adjustScore(
  gameId: string,
  lbId: string,
  cycleId: string,
  entityId: string,
  body: ScoreAdjustInput,
): Promise<void> {
  await apiClient.patch(scorePath(gameId, lbId, cycleId, entityId), body);
}

// ===== Audit =====

export interface ListAuditPage {
  audit: AuditEntry[];
  cursor: string;
}

export async function listAudit(
  gameId: string,
  lbId: string,
  cycleId: string,
  filter: AuditListFilter = {},
): Promise<ListAuditPage> {
  const qs = new URLSearchParams();
  qs.set("cycle", cycleId);
  if (filter.entity_id) qs.set("entity", filter.entity_id);
  if (filter.reject_reason) qs.set("reject_reason", filter.reject_reason);
  if (filter.cursor) qs.set("cursor", filter.cursor);
  if (filter.limit) qs.set("limit", String(filter.limit));
  const r = await apiClient.get<ListAuditPage>(
    `${lbPath(gameId, lbId)}/audit?${qs.toString()}`,
  );
  return r.data;
}

// ===== Rewards =====

export async function listRewards(gameId: string, lbId: string): Promise<Reward[]> {
  const r = await apiClient.get<{ rewards: Reward[] }>(
    `${lbPath(gameId, lbId)}/rewards`,
  );
  return r.data.rewards;
}

export async function getReward(
  gameId: string,
  lbId: string,
  rid: string,
): Promise<Reward> {
  const r = await apiClient.get<Reward>(rewardPath(gameId, lbId, rid));
  return r.data;
}

export async function createReward(
  gameId: string,
  lbId: string,
  input: CreateRewardInput,
): Promise<Reward> {
  const r = await apiClient.post<Reward>(
    `${lbPath(gameId, lbId)}/rewards`,
    input,
  );
  return r.data;
}

export async function updateReward(
  gameId: string,
  lbId: string,
  rid: string,
  input: UpdateRewardInput,
): Promise<Reward> {
  const r = await apiClient.patch<Reward>(rewardPath(gameId, lbId, rid), input);
  return r.data;
}

export async function deleteReward(
  gameId: string,
  lbId: string,
  rid: string,
): Promise<void> {
  await apiClient.delete(rewardPath(gameId, lbId, rid));
}

// ===== React Query keys =====

export const lbKeys = {
  list: (gameId: string, filter: ListFilter) =>
    ["leaderboards", gameId, filter] as const,
  detail: (gameId: string, lbId: string) =>
    ["leaderboard", gameId, lbId] as const,
  cycles: (gameId: string, lbId: string, opts: { cursor?: string; limit?: number } = {}) =>
    ["leaderboard.cycles", gameId, lbId, opts] as const,
  cycle: (gameId: string, lbId: string, cycleId: string) =>
    ["leaderboard.cycle", gameId, lbId, cycleId] as const,
  buckets: (gameId: string, lbId: string, cycleId: string) =>
    ["leaderboard.buckets", gameId, lbId, cycleId] as const,
  top: (gameId: string, lbId: string, cycleId: string, opts: ListTopOpts = {}) =>
    ["leaderboard.top", gameId, lbId, cycleId, opts] as const,
  audit: (gameId: string, lbId: string, cycleId: string, filter: AuditListFilter) =>
    ["leaderboard.audit", gameId, lbId, cycleId, filter] as const,
  rewards: (gameId: string, lbId: string) =>
    ["leaderboard.rewards", gameId, lbId] as const,
  reward: (gameId: string, lbId: string, rid: string) =>
    ["leaderboard.reward", gameId, lbId, rid] as const,
};
